import { type ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/colors';
import { MemberRole, type Member } from '../types';
import { RoleCell } from '../components/cells/RoleCell';
import { AreaCell } from '../components/cells/AreaCell';
import { ExpirationCell } from '../components/cells/ExpirationCell';
import { StatusCell } from '../components/cells/StatusCell';
import { MemberRowActions } from '../components/MemberRowActions';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';

interface MemberColumnsOptions {
  onStatusChange: (memberId: string, activate: boolean) => void;
  isLoading?: (memberId: string) => boolean;
  t: TFunction;
}

function CreatedAtCell({ member }: { member: Member }) {
  const formatInstant = useInstantFormat();
  return <span className="text-sm text-muted-foreground">{formatInstant(member.createdAt)}</span>;
}

export function getMemberColumns(options: MemberColumnsOptions): ColumnDef<Member, unknown>[] {
  const { onStatusChange, isLoading, t } = options;

  return [
    {
      accessorKey: 'displayName',
      header: t('table.member'),
      meta: { cellClassName: 'flex-[2] min-w-0' },
      cell: ({ row }) => {
        const member = row.original;
        return (
          <div className="flex items-center gap-3">
            <UserAvatar
              name={member.displayName}
              color={getUserColor(member.userId || member.id)}
              src={member.avatarUrl ?? undefined}
              size="sm"
            />
            <div>
              <div className="font-medium text-foreground">
                {member.displayName}
                {member.isCurrentUser && (
                  <span className="ml-2 text-xs text-muted-foreground">{t('you')}</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">{member.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'role',
      header: t('table.role'),
      cell: ({ row }) => <RoleCell member={row.original} t={t} />,
    },
    {
      accessorKey: 'areaId',
      header: t('table.area'),
      cell: ({ row }) => <AreaCell member={row.original} t={t} />,
    },
    {
      accessorKey: 'expiresAt',
      header: t('table.expiration'),
      cell: ({ row }) => <ExpirationCell member={row.original} t={t} />,
    },
    {
      accessorKey: 'isActive',
      header: t('table.status'),
      meta: { cellClassName: 'flex-none w-24' },
      cell: ({ row }) => {
        const member = row.original;
        return (
          <StatusCell
            member={member}
            onChange={(activate) => onStatusChange(member.id, activate)}
            isLoading={isLoading?.(member.id)}
            t={t}
          />
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: t('table.joinedAt'),
      meta: { cellClassName: 'flex-none w-28' },
      cell: ({ row }) => <CreatedAtCell member={row.original} />,
    },
    {
      id: 'actions',
      header: undefined,
      meta: { cellClassName: 'flex-none w-12 flex justify-end' },
      cell: ({ row }) => {
        const member = row.original;
        if (member.role === MemberRole.Owner || member.isCurrentUser) return null;
        return <MemberRowActions member={member} />;
      },
    },
  ];
}
